// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FilterMenuButton } from '@/components/filter-menu-button';
import { FilterMenuItem } from '@/components/filter-menu-item';
import { useDisclosure } from '@/hooks/use-disclosure';
import { RO_MODIFIER_LABELS } from '@/return-order/constants/filter-config';
import {
  ROSubstatus,
  roSubstatusSchema,
} from '@/return-order/constants/status';
import { Menu } from '@mui/material';
import { useRef } from 'react';

interface Props {
  /**
   * The current modifier filter values.
   */
  value: ROSubstatus[];

  /**
   * A callback fired when the filter values change.
   */
  onChange: (value: ROSubstatus[]) => void;
}

/**
 * A controlled component that allows the user to filter return orders by modifier.
 * Multiple options can be selected at the same time.
 */
export function ReturnOrderModifierFilter(props: Props) {
  const menu = useDisclosure();
  const anchorElementRef = useRef<HTMLButtonElement>(null);

  const options = roSubstatusSchema.options.map((modifier) => ({
    id: modifier,
    label: RO_MODIFIER_LABELS[modifier],
    isActive: props.value.includes(modifier),
  }));

  const handleChangeOption = (id: ROSubstatus, isActive: boolean) => {
    if (isActive) {
      props.onChange([...props.value, id]);
    } else {
      props.onChange(props.value.filter((v) => v !== id));
    }
  };

  const count = options.filter((option) => option.isActive).length;

  return (
    <>
      <FilterMenuButton
        ref={anchorElementRef}
        count={count}
        isDisabled={false}
        onClick={menu.open}
      >
        Status level
      </FilterMenuButton>
      <Menu
        open={menu.isOpen}
        anchorEl={anchorElementRef.current}
        onClose={menu.close}
      >
        {options.map((option) => (
          <FilterMenuItem
            key={option.id}
            isActive={option.isActive}
            onChange={(isActive) => handleChangeOption(option.id, isActive)}
          >
            {option.label}
          </FilterMenuItem>
        ))}
      </Menu>
    </>
  );
}
